/**
 * Drawer latéral générique (gauche ou droite) avec FAB de déclenchement.
 *
 * Conçu pour les pages "plein écran" (Game, Training) sur mobile compact :
 * permet d'exposer une sidebar (réglages, timeline, etc.) sans amputer
 * l'espace de jeu, via un bouton flottant et un panneau coulissant.
 *
 * Variante générique de `GameDrawer` qui ne supporte que la gauche.
 */

import { useState, useCallback, useEffect, type ReactNode } from 'react';
import { BackArrowIcon } from './Icons';

export type DrawerSide = 'left' | 'right';

interface SideDrawerProps {
  /** Côté du drawer */
  side: DrawerSide;
  /** Icône du FAB (composant React) */
  fabIcon: ReactNode;
  /** aria-label du FAB */
  fabLabel: string;
  /** Badge facultatif (compteur de notifications) */
  badgeCount?: number;
  /** Titre dans le header du panneau */
  title: string;
  /** Contenu du drawer */
  children: ReactNode;
  /** Action optionnelle dans le header (ex: "Quitter") */
  headerAction?: ReactNode;
  /** Décalage vertical du FAB (px). Permet d'empiler plusieurs drawers. */
  fabOffset?: number;
}

export default function SideDrawer({
  side,
  fabIcon,
  fabLabel,
  badgeCount = 0,
  title,
  children,
  headerAction,
  fabOffset = 0,
}: SideDrawerProps) {
  const [open, setOpen] = useState(false);

  const toggle = useCallback(() => setOpen(v => !v), []);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, close]);

  const fabStyle = side === 'left'
    ? { left: 'calc(16px + env(safe-area-inset-left, 0px))', bottom: `calc(var(--chrome-bottom, var(--nav-offset, calc(var(--nav-height-compact) + var(--safe-area-bottom)))) + 16px + ${fabOffset}px)` }
    : { right: 'calc(16px + env(safe-area-inset-right, 0px))', bottom: `calc(var(--chrome-bottom, var(--nav-offset, calc(var(--nav-height-compact) + var(--safe-area-bottom)))) + 16px + ${fabOffset}px)` };

  return (
    <>
      <button
        className={`side-drawer-toggle side-drawer-toggle-${side}`}
        style={fabStyle}
        onClick={toggle}
        aria-label={fabLabel}
      >
        {fabIcon}
        {badgeCount > 0 && (
          <span className="side-drawer-badge">{badgeCount > 99 ? '99+' : badgeCount}</span>
        )}
      </button>

      <div
        className={`side-drawer-backdrop ${open ? 'open' : ''}`}
        onClick={close}
      />

      <div className={`side-drawer-panel side-drawer-panel-${side} ${open ? 'open' : ''}`}>
        <div className="side-drawer-header">
          <button
            className="side-drawer-close"
            onClick={close}
            aria-label="Fermer le panneau"
          >
            <BackArrowIcon size={18} />
          </button>
          <span className="side-drawer-header-title">{title}</span>
          {headerAction}
        </div>
        <div className="side-drawer-content">
          {children}
        </div>
      </div>
    </>
  );
}
